import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ChampionshipProvider } from "@/context/ChampionshipContext";

import PublicLayout from "@/components/layout/PublicLayout";
import ScrollToTop from "@/components/ScrollToTop";
import HomePage from "@/pages/HomePage";
import TeamsPage from "@/pages/TeamsPage";
import NewsPage from "@/pages/NewsPage";
import NewsArticlePage from "@/pages/NewsArticlePage";
import InfoPage from "@/pages/InfoPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import CaptainDashboardPage from "@/pages/CaptainDashboardPage";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverviewPage from "@/pages/admin/AdminOverviewPage";
import AdminNewsPage from "@/pages/admin/AdminNewsPage";
import AdminInfoPagesPage from "@/pages/admin/AdminInfoPagesPage";
import AdminSponsorsPage from "@/pages/admin/AdminSponsorsPage";
import AdminCaptainsPage from "@/pages/admin/AdminCaptainsPage";
import AdminChampionshipsPage from "@/pages/admin/AdminChampionshipsPage";
import AdminSiteAssetsPage from "@/pages/admin/AdminSiteAssetsPage";
import AdminContactsPage from "@/pages/admin/AdminContactsPage";
import AdminPlayersPage from "@/pages/admin/AdminPlayersPage";
import AdminLivePage from "@/pages/admin/AdminLivePage";
import LiveRedirect from "@/pages/LiveRedirect";
import PartnersPage from "@/pages/PartnersPage";
import ContactPage from "@/pages/ContactPage";

function RequireAuth({ children, role }) {
  const { user, loading } = useAuth() || {};
  const location = useLocation();
  if (loading) return <div className="p-10 text-muted-foreground">Loading…</div>;
  if (!user) {
    // Parity with the axios 401 interceptor: preserve the intended path and
    // surface a 'session expired' toast on LoginPage (when triggered by a
    // full-page reload of a protected route after the cookie expired).
    try {
      sessionStorage.setItem("auth_session_expired", "1");
    } catch {
      // sessionStorage unavailable — ignore.
    }
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <ChampionshipProvider>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route index element={<HomePage />} />
                <Route path="/teams" element={<TeamsPage />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/news/:slug" element={<NewsArticlePage />} />
                <Route path="/info/:slug" element={<InfoPage />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/live" element={<LiveRedirect />} />
                <Route path="/live/*" element={<LiveRedirect />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route
                  path="/captain"
                  element={
                    <RequireAuth>
                      <CaptainDashboardPage />
                    </RequireAuth>
                  }
                />
              </Route>

              <Route
                path="/admin"
                element={
                  <RequireAuth role="admin">
                    <AdminLayout />
                  </RequireAuth>
                }
              >
                <Route index element={<AdminOverviewPage />} />
                <Route path="news" element={<AdminNewsPage />} />
                <Route path="info" element={<AdminInfoPagesPage />} />
                <Route path="sponsors" element={<AdminSponsorsPage />} />
                <Route path="contacts" element={<AdminContactsPage />} />
                <Route path="site-assets" element={<AdminSiteAssetsPage />} />
                <Route path="captains" element={<AdminCaptainsPage />} />
                <Route path="players" element={<AdminPlayersPage />} />
                <Route path="live" element={<AdminLivePage />} />
                <Route path="championships" element={<AdminChampionshipsPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ChampionshipProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
